import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { actions as ConnectionActions } from "../../../Ducks/Connection.duck";
import { actions as EventsActions } from "../../../Ducks/Events.duck";
import { isEmpty, Uuid } from "app/utils";
import Grid from "@mui/material/Grid";
import EventsToolbar from "../../../Components/EventsToolbar/EventsToolbar";
import EventsTable from "../../../Packages/InsuranceBrydge/Components/EventsTable";
import { ShowEventDetailsDialog } from "../../../Packages/InsuranceBrydge/Components/ShowEventDetailsDialog";
import { SnackBar } from "app/components";
import { FormattedMessage } from "react-intl";
import { useBreadcrumb } from "app/components/Breadcrumbs";
import { ConnectionNoteInput } from "../../../Components/ConnectionNoteInput";
import { HasAccessTo } from "app/components/Authorization";
import { CONNECTION } from "../../../../../common/Authorization/entities";
import { NOTE } from "../../../../../common/Authorization/permissions";
import { DivContentStyled } from "./EventsTab.styles";
import { ProductPensionSchemeEnum } from "../../../../../types";
import { useDialog, useSnakeBar } from "../../../../../hooks";
import { ConfirmDialogWithTextInput } from "../../../../../components";
import { TranslationKeys } from "../../../../../translations";
import { Typography } from "@mui/material";
import {
  useCreateComparisonFromEventsMutation,
  useCountComparisonCompletedQuery,
  useComparisonCompleteMutation,
  useSearchComparisonsActiveQuery
} from "../../../../../hooks/api/baseline-measurements";
import { useIntl } from "react-intl";
import { ConnectionInsurerPackageTypeEnum } from "../../../../../types";
import { useEmployerDetailContext } from "../../../../Employers/Pages/EmployerDetailPage/EmployerDetailContext";
const hasNaverrekenenPackage = (connection) => {
  if (!connection?.packages?.length) return false;
  return connection.packages.some((pkg) => pkg.packageType === ConnectionInsurerPackageTypeEnum.Naverrekenen);
};
const EventsTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const { connectionId, employerId } = useParams();
  const [showEventDetails, setShowEventDetails] = useState(null);
  const [allEventsArchived, setAllEventsArchived] = useState(false);
  const [allEventsUnarchived, setAllEventsUnarchived] = useState(false);
  const [filtersCheck, setFiltersCheck] = useState(true);
  const { items, page, pageSize, totalCount, filters, selectedRowsIds, archivedRows, unarchivedRows, searchQuery } = useSelector((state) => state.EventsReducer);
  const { loading: appLoading, showSnackBar } = useSelector((state) => state.AppReducer);
  const { loading: connectionLoading, item: connection } = useSelector((state) => state.ConnectionReducer);
  const { employerDetail: employer } = useEmployerDetailContext();
  const isRegularConnection = connection?.product?.pensionScheme === ProductPensionSchemeEnum.Regular;
  const isNaverrekenenConnection = hasNaverrekenenPackage(connection);
  const isPensioenbijConnection = Boolean(employer?.isPensioenbijIntegration);
  const { showErrorSnakeBar } = useSnakeBar();
  const {
    dialogState: baselineMeasurementLockState,
    closeDialog: closeBaselineMeasurementLockDialog,
    openDialog: openBaselineMeasurementLockDialog
  } = useDialog(false);
  const { data: comparisonCompletedResponse, isLoading: isCountComparisonsTotalLoading } = useCountComparisonCompletedQuery({ connectionId });
  const { data: comparisonsActiveResponse, isLoading: isSearchingComparisonsActive } = useSearchComparisonsActiveQuery({
    connectionId
  });
  const { mutate: createComparisonFromEvents } = useCreateComparisonFromEventsMutation();
  const { mutate: completeComparison } = useComparisonCompleteMutation();
  const [pendingAction, setPendingAction] = useState(null);
  useEffect(() => {
    dispatch(ConnectionActions.requestData(connectionId));
    dispatch(EventsActions.setConnectionId(connectionId));
    dispatch(EventsActions.clearEvents());
    dispatch(EventsActions.resetFilters());
  }, [connectionId, dispatch]);
  useEffect(() => {
    if (!isEmpty(filters.eventType) || !isEmpty(filters["eventDate[before]"]) || !isEmpty(filters["eventDate[after]"]) || filters.archived !== false || searchQuery !== "") {
      setFiltersCheck(false);
    } else {
      setFiltersCheck(true);
    }
  }, [filters, searchQuery]);
  useEffect(() => {
    if (archivedRows > 0 && unarchivedRows === 0) {
      setAllEventsArchived(true);
      setAllEventsUnarchived(false);
    } else if (archivedRows === 0 && unarchivedRows > 0) {
      setAllEventsArchived(false);
      setAllEventsUnarchived(true);
    } else {
      setAllEventsArchived(false);
      setAllEventsUnarchived(false);
    }
  }, [archivedRows, unarchivedRows]);
  const handleBaselineMeasurementLockConfirm = useCallback(() => {
    if (pendingAction && connectionId) {
      const comparisonId = Uuid.newV4();
      createComparisonFromEvents(
        {
          comparisonId,
          connectionId,
          eventIds: pendingAction === "processAll" ? [] : selectedRowsIds,
          selectAll: pendingAction === "processAll"
        },
        {
          onSuccess: () => {
            completeComparison(
              { comparisonId },
              {
                onSuccess: () => {
                  closeBaselineMeasurementLockDialog();
                  if (pendingAction === "processAll") {
                    dispatch(EventsActions.processSelectedEvents({ selectAll: true }));
                  } else {
                    dispatch(EventsActions.processSelectedEvents({ selectAll: false }));
                  }
                  setPendingAction(null);
                },
                onError: () => {
                  showErrorSnakeBar({
                    method: "completeComparison",
                    message: intl.formatMessage({
                      id: TranslationKeys["baseline_comparison_actions_create_ko"]
                    })
                  });
                  setPendingAction(null);
                }
              }
            );
          },
          onError: () => {
            showErrorSnakeBar({
              method: "createComparisonFromEvents",
              message: intl.formatMessage({
                id: TranslationKeys["baseline_comparison_actions_create_ko"]
              })
            });
            setPendingAction(null);
          }
        }
      );
    }
  }, [
    pendingAction,
    connectionId,
    selectedRowsIds,
    createComparisonFromEvents,
    completeComparison,
    closeBaselineMeasurementLockDialog,
    dispatch,
    showErrorSnakeBar,
    intl
  ]);
  const handleBaselineMeasurementLockCancel = useCallback(() => {
    closeBaselineMeasurementLockDialog();
    setPendingAction(null);
  }, [closeBaselineMeasurementLockDialog]);
  const handlePageChange = useCallback(
    (event, newPage) => {
      dispatch(EventsActions.changePage(newPage + 1));
    },
    [dispatch]
  );
  const handleRowsPerPageChange = useCallback(
    (event) => {
      dispatch(EventsActions.setPageSize(event.target.value));
    },
    [dispatch]
  );
  const handleToggleEvent = useCallback(
    (event) => {
      dispatch(EventsActions.toggleEvent(event));
    },
    [dispatch]
  );
  const handleToggleAllEvents = useCallback(
    (events) => {
      dispatch(EventsActions.toggleAllEvents(events));
    },
    [dispatch]
  );
  const applyFilter = useCallback(
    (key, value) => {
      dispatch(EventsActions.applyFilter(key, value));
    },
    [dispatch]
  );
  const resetFilters = useCallback(() => {
    dispatch(EventsActions.resetFilters());
    setFiltersCheck(true);
  }, [dispatch]);
  const archiveAllEvents = useCallback(() => {
    dispatch(EventsActions.archiveSelectedEvents(true));
  }, [dispatch]);
  const archiveEvents = useCallback(() => {
    dispatch(EventsActions.archiveSelectedEvents(false));
  }, [dispatch]);
  const unArchiveAllEvents = useCallback(() => {
    dispatch(EventsActions.unArchiveSelectedEvents(true));
  }, [dispatch]);
  const unArchiveEvents = useCallback(() => {
    dispatch(EventsActions.unArchiveSelectedEvents(false));
  }, [dispatch]);
  const createDraftBatch = React.useCallback(
    (selectAll) => {
      dispatch(EventsActions.processSelectedEvents({ selectAll, isDraft: true }));
    },
    [dispatch]
  );
  const processAllEvents = useCallback(() => {
    if (comparisonCompletedResponse && comparisonCompletedResponse.totalItems <= 0 && comparisonsActiveResponse && comparisonsActiveResponse.totalItems <= 0 && !isNaverrekenenConnection) {
      setPendingAction("processAll");
      openBaselineMeasurementLockDialog();
    } else {
      dispatch(EventsActions.processSelectedEvents({ selectAll: true }));
    }
  }, [
    dispatch,
    comparisonCompletedResponse,
    openBaselineMeasurementLockDialog,
    comparisonsActiveResponse,
    isNaverrekenenConnection
  ]);
  const processEvents = useCallback(() => {
    if (comparisonCompletedResponse && comparisonCompletedResponse.totalItems <= 0 && comparisonsActiveResponse && comparisonsActiveResponse.totalItems <= 0 && !isNaverrekenenConnection) {
      setPendingAction("process");
      openBaselineMeasurementLockDialog();
    } else {
      dispatch(EventsActions.processSelectedEvents({ selectAll: false }));
    }
  }, [
    dispatch,
    comparisonCompletedResponse,
    openBaselineMeasurementLockDialog,
    comparisonsActiveResponse,
    isNaverrekenenConnection
  ]);
  const processAllPensioenbijMutations = useCallback(() => {
    dispatch(EventsActions.processSelectedPensioenbijMutations({ selectAll: true }));
  }, [dispatch]);
  const processPensioenbijMutations = useCallback(() => {
    dispatch(EventsActions.processSelectedPensioenbijMutations({ selectAll: false }));
  }, [dispatch]);
  const downloadAllEvents = useCallback(() => {
    dispatch(EventsActions.downloadSelectedEvents(true));
  }, [dispatch]);
  const downloadEvents = useCallback(() => {
    dispatch(EventsActions.downloadSelectedEvents(false));
  }, [dispatch]);
  const handleSearch = useCallback(
    (event) => {
      dispatch(EventsActions.search(event.target.value));
    },
    [dispatch]
  );
  const handleShowDetails = useCallback((event) => {
    setShowEventDetails(event);
  }, []);
  const archiveSamePeriodEvents = useCallback(() => {
    dispatch(EventsActions.archiveSamePeriodEvents());
  }, [dispatch]);
  useBreadcrumb("connectionDetails.events");
  const enabledIfAccessToConnectionNote = HasAccessTo(CONNECTION, NOTE);
  const handleComparisonAllEvents = useCallback(() => {
    if (employerId && connectionId) {
      const comparisonId = Uuid.newV4();
      createComparisonFromEvents(
        {
          comparisonId,
          connectionId,
          eventIds: [],
          selectAll: true
        },
        {
          onSuccess: () => {
            navigate(
              `/employers/${employerId}/connections/${connectionId}/baseline-measurements/comparisons/${comparisonId}`
            );
          },
          onError: () => {
            showErrorSnakeBar({ method: "createComparisonFromEvents", message: "Impossible Create Comparison" });
          }
        }
      );
    }
  }, [employerId, connectionId, createComparisonFromEvents, showErrorSnakeBar, navigate]);
  const handleComparisonEvents = useCallback(() => {
    if (employerId && connectionId) {
      const comparisonId = Uuid.newV4();
      createComparisonFromEvents(
        {
          comparisonId,
          connectionId,
          eventIds: selectedRowsIds,
          selectAll: false
        },
        {
          onSuccess: () => {
            navigate(
              `/employers/${employerId}/connections/${connectionId}/baseline-measurements/comparisons/${comparisonId}`
            );
          },
          onError: () => {
            showErrorSnakeBar({ method: "createComparisonFromEvents", message: "Impossible Create Comparison" });
          }
        }
      );
    }
  }, [employerId, connectionId, createComparisonFromEvents, selectedRowsIds, navigate, showErrorSnakeBar]);
  const handleActiveComparison = useCallback(() => {
    if (comparisonsActiveResponse && employerId && connectionId) {
      const comparisonId = comparisonsActiveResponse.items[0].comparisonId;
      navigate(
        `/employers/${employerId}/connections/${connectionId}/baseline-measurements/comparisons/${comparisonId}`
      );
    }
  }, [comparisonsActiveResponse, connectionId, employerId, navigate]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !!baselineMeasurementLockState && !isCountComparisonsTotalLoading && !isSearchingComparisonsActive && /* @__PURE__ */ React.createElement(
    ConfirmDialogWithTextInput,
    {
      open: !!baselineMeasurementLockState,
      title: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.baseline_title }),
      label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.insurers_connectionName }),
      errorMessage: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.connections_confirmDeleteMessage_invalidAnswer }),
      solution: connection?.connectionName,
      onClose: handleBaselineMeasurementLockCancel,
      onConfirm: handleBaselineMeasurementLockConfirm
    },
    /* @__PURE__ */ React.createElement(Typography, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.baseline_confirmMessage_paragraph1 })),
    /* @__PURE__ */ React.createElement(Typography, { lineHeight: 2 }, /* @__PURE__ */ React.createElement(
      FormattedMessage,
      {
        id: TranslationKeys.baseline_confirmMessage_paragraph2,
        values: { connectionName: connection?.connectionName, b: (chunks) => /* @__PURE__ */ React.createElement("b", null, chunks) }
      }
    ))
  ), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 5 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(
    EventsToolbar,
    {
      isRegularConnection,
      connectionId,
      handleSearch,
      isPensioenbijConnection,
      filters,
      filtersCheck,
      resetFilters,
      applyFilter,
      archiveEvents,
      comparisonEvents: handleComparisonEvents,
      comparisonAllEvents: handleComparisonAllEvents,
      comparisonActive: handleActiveComparison,
      hasComparisonActive: comparisonsActiveResponse && comparisonsActiveResponse.totalItems > 0,
      unArchiveEvents,
      createDraftBatch,
      processEvents,
      processPensioenbijMutations,
      downloadEvents,
      archiveAllEvents,
      unArchiveAllEvents,
      processAllEvents,
      processAllPensioenbijMutations,
      downloadAllEvents,
      hasSelectedItems: selectedRowsIds.length > 0,
      allEventsArchived,
      allEventsUnarchived,
      archiveSamePeriodEvents,
      initialSearchQuery: searchQuery
    }
  ), /* @__PURE__ */ React.createElement(DivContentStyled, null), /* @__PURE__ */ React.createElement(
    EventsTable,
    {
      events: items,
      page: page - 1,
      rowsPerPage: pageSize,
      totalCount,
      loading: connectionLoading || appLoading,
      selectedRowsIds,
      isRegularConnection,
      handlePageChange,
      handleRowsPerPageChange,
      handleToggleEvent,
      handleToggleAllEvents,
      handleShowDetails
    }
  ), showEventDetails && /* @__PURE__ */ React.createElement(
    ShowEventDetailsDialog,
    {
      open: !!showEventDetails,
      onClose: () => setShowEventDetails(null),
      event: showEventDetails
    }
  )), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 6 }, enabledIfAccessToConnectionNote && /* @__PURE__ */ React.createElement(ConnectionNoteInput, { connectionId }))), /* @__PURE__ */ React.createElement(SnackBar, { message: /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.tooManyEvents" }), open: showSnackBar }));
};
export default EventsTab;
